import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, DollarSign, Mic, CheckCircle2, Rocket, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20_000; // 20s

const ICONS = {
  invoice_paid: DollarSign,
  episode_published: Mic,
  project_complete: CheckCircle2,
  content_published: Rocket,
};
const ACCENTS = {
  invoice_paid: "#22c55e",
  episode_published: "#a78bfa",
  project_complete: "#00f0ff",
  content_published: "#ff0055",
};
const LABELS = {
  invoice_paid: "Invoice Paid",
  episode_published: "Episode Published",
  project_complete: "Project Complete",
  content_published: "Content Published",
};

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const MondayEventsFeed = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const panelRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/monday-events?limit=25`, {
        headers: authHeaders,
      });
      setEvents(data.events || []);
      setUnread(data.unread_count || 0);
      setEnabled(!!data.enabled);
    } catch (err) {
      // Non-fatal — polling will retry every 20s. Debug only.
      if (process.env.NODE_ENV !== "production") console.debug("MondayEventsFeed fetch failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
    const id = setInterval(fetchEvents, POLL_MS);
    return () => clearInterval(id);
  }, [fetchEvents]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markOne = async (id) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, read: true } : e)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.post(
        `${API}/monday-events/mark-read`,
        { ids: [id] },
        { headers: authHeaders },
      );
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("markOne failed:", err?.message);
    }
  };

  const markAll = async () => {
    setEvents((prev) => prev.map((e) => ({ ...e, read: true })));
    setUnread(0);
    try {
      await axios.post(
        `${API}/monday-events/mark-read`,
        {},
        { headers: authHeaders },
      );
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("markAll failed:", err?.message);
    }
  };

  if (!enabled) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Studio notifications"
        data-testid="monday-events-bell"
        className="relative w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
      >
        <Bell size={15} className={unread > 0 ? "text-[#00f0ff]" : "text-white/80"} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff0055] text-[10px] font-bold text-white flex items-center justify-center border-2 border-[#05050a]">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            data-testid="monday-events-panel"
            className="fixed inset-x-3 top-[68px] w-auto sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[360px] rounded-2xl bg-[#0a0612] border border-white/10 shadow-[0_30px_120px_-30px_rgba(0,240,255,0.25)] overflow-hidden z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff]">
                  Studio Updates
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  Live from monday.com
                </div>
              </div>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  data-testid="monday-events-mark-all"
                  className="text-[11px] text-[#00f0ff] hover:text-white transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={18} className="animate-spin text-white/40" />
                </div>
              ) : events.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center px-6">
                  <Bell size={26} className="text-white/25 mb-2" />
                  <div className="text-sm text-white/60">No updates yet</div>
                  <div className="text-xs text-white/30 mt-1">
                    Status changes on your monday.com boards will surface here.
                  </div>
                </div>
              ) : (
                events.map((e) => {
                  const Icon = ICONS[e.event_type] || Bell;
                  const accent = ACCENTS[e.event_type] || "#00f0ff";
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => !e.read && markOne(e.id)}
                      data-testid={`monday-event-${e.id}`}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-white/5 hover:bg-white/[0.03] transition-colors ${
                        !e.read ? "bg-white/[0.02]" : ""
                      }`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center"
                        style={{
                          background: `${accent}18`,
                          border: `1px solid ${accent}40`,
                          color: accent,
                        }}
                      >
                        <Icon size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                            style={{ color: accent }}
                          >
                            {LABELS[e.event_type] || e.event_type}
                          </span>
                          {!e.read && (
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: accent }}
                            />
                          )}
                        </div>
                        <div className="text-sm text-white truncate mt-0.5 font-medium">
                          {e.item_name}
                        </div>
                        <div className="text-[11px] text-white/40 mt-0.5">
                          {e.board_name} · {timeAgo(e.triggered_at)}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="px-4 py-2 border-t border-white/10 text-center">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/30">
                Powered by monday.com webhooks
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MondayEventsFeed;
